import React, { useEffect, useMemo, useState, useRef } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';

import useCheckoutState, { checkoutStates } from '../../hooks/useCheckoutState';
import ErrorMessage from './components/ErrorMessage';
import FormField from './components/FormField';
import Button from '../../components/Button';
import CouponField from './components/CouponField';
import PayWhatYouWantField from './components/PayWhatYouWantField';
import Spinner from '../../components/Spinner';
import useCheckoutFormHelpers from '../../hooks/useCheckoutFormHelpers';
import useCheckoutValidation from '../../hooks/useCheckoutValidation';
import useCheckoutHelpers from '../../hooks/useCheckoutHelpers';
import PaymentMethods from './components/PaymentMethods';
import getPayButtonText from './lib/getPayButtonText';
import clsx from 'clsx';
import ExpressCheckoutElement from './ExpressCheckoutElement';
import useRecaptcha from '../../hooks/useRecaptcha';
import getButtonTextColor from '../../lib/getButtonTextColor';
import getButtonBackgroundColor from '../../lib/getButtonBackgroundColor';
import useCheckoutUpsell from '../../hooks/useCheckoutUpsell';
import useCartCost from '../../hooks/useCartCost';
import PriceBreakdown from '../PriceBreakdown';
import { PAGE_TYPES } from '../../enums/page-types';
import checkShouldShowPaymentMethods from '../../lib/checkShouldShowPaymentMethods';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import PaymentOptions from './PaymentOptions';
import useCheckoutCoupon from '@repo/checkout/hooks/useCheckoutCoupon';
import useSubmitDisabled from '../../hooks/useSubmitDisabled';

const CheckoutForm = () => {
  const {
    formError,
    setFormError,
    state,
    form,
    product,
    checkout,
    payWhatYouWantPrice,
    setState,
    seller,
    formRef,
    query,
    isScreenshot,
    isUpsell,
    isUpsellRetry,
    showCouponCodeField,
    setShowCouponCodeField,
    paymentOption,
    isMeasurer,
    hasFocusedRef,
    draftCheckout,
  } = useCheckoutState();
  const stripe = useStripe();
  const elements = useElements();
  const { validateCoupon } = useCheckoutCoupon();
  const { executeRecaptcha } = useRecaptcha();
  const { declineUpsell } = useCheckoutUpsell();

  const { setFormField, setFormStateByKey } = useCheckoutFormHelpers();
  const {
    createPayment,
    createSubscription,
    createSubmission,
    updateDraftCharge,
    updateDraftChargeDebounced,
    updateDraftChargeInstant,
    cancelUpdateDraftCharge,
  } = useCheckoutHelpers();
  const { validateMinimumChargeAmount, validateHasStock, validateVariants } =
    useCheckoutValidation();
  const { totalDueAmount, subtotalAmount, productPriceAmount, coupon } = useCartCost();

  const [paymentElement, setPaymentElement] = useState(null);
  const [paymentElementComplete, setPaymentElementComplete] = useState(false);

  const { submitIsDisabled } = useSubmitDisabled(paymentElement, paymentElementComplete);

  const [expressCheckoutLoaded, setExpressCheckoutLoaded] = useState(false);
  const prevFormFieldsRef = useRef();
  const prevFormVariantsRef = useRef();
  const prevPaymentOptionRef = useRef();
  const prevCouponRef = useRef();
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    async function setCoupon(couponCode) {
      try {
        await validateCoupon(couponCode);
        setShowCouponCodeField(true);
      } catch (error) {
        // no-op
      }
    }

    if (!checkout.showCouponCodeField || isUpsell) return;

    if (query.coupon) {
      setCoupon(query.coupon);
    } else if (draftCheckout?.coupon?.code) {
      setCoupon(draftCheckout.coupon.code);
    }
  }, [query, draftCheckout]);

  // Centralized draft charge update for immediate-action fields
  useEffect(() => {
    if (isUpsell) {
      return;
    }

    // Only update draft for actual changes, not initial render
    const fieldsChanged =
      prevFormFieldsRef.current &&
      JSON.stringify(prevFormFieldsRef.current) !== JSON.stringify(form.fields);
    const variantsChanged =
      prevFormVariantsRef.current &&
      JSON.stringify(prevFormVariantsRef.current) !== JSON.stringify(form.variants);
    const paymentOptionChanged =
      prevPaymentOptionRef.current && prevPaymentOptionRef.current?.type !== paymentOption?.type;
    const couponChanged = prevCouponRef.current !== coupon;

    // Use instant update for variants (immediate feedback for user selections)
    if (variantsChanged) {
      updateDraftChargeInstant();
    } else if (fieldsChanged || paymentOptionChanged || couponChanged) {
      // Use debounced version for form fields to prevent excessive API calls while typing
      updateDraftChargeDebounced();
    }

    // Update refs
    prevFormFieldsRef.current = form.fields;
    prevFormVariantsRef.current = form.variants;
    prevPaymentOptionRef.current = paymentOption;
    prevCouponRef.current = coupon;
  }, [
    form.fields,
    form.variants,
    paymentOption,
    coupon,
    updateDraftChargeDebounced,
    updateDraftChargeInstant,
  ]);

  const showPaymentMethods = checkShouldShowPaymentMethods({
    paymentOption,
    amount: totalDueAmount,
    checkout,
    product,
    form,
    isUpsell,
    isUpsellRetry,
    coupon,
  });

  useEffect(() => {
    if (!elements) return;

    // unset payment element if payment methods are not (no longer) shown
    if (paymentElement && !showPaymentMethods) {
      setPaymentElement(null);
      setPaymentElementComplete(false);
      return;
    }

    if (!showPaymentMethods) return;

    // repeat until we have a payment element
    const interval = setInterval(() => {
      const element = elements?.getElement('payment') || elements?.getElement('card');

      if (element) {
        setPaymentElement(element);
        clearInterval(interval);
      }
    }, 1000);
  }, [elements, showPaymentMethods, paymentElement]);

  useEffect(() => {
    if (!paymentElement) return;

    paymentElement.on('change', event => {
      if (event.complete) {
        setPaymentElementComplete(true);
      } else {
        setPaymentElementComplete(false);
      }
    });
  }, [paymentElement]);

  useEffect(() => {
    return () => {
      isSubmittingRef.current = false;
    };
  }, []);

  async function submitForm() {
    if (submitIsDisabled) return;
    if (state === checkoutStates.SUBMITTING) return;

    // Prevent double submissions with immediate ref check
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    /**
     * Cancel the creation or updating of a draft. This prevents an issue where the draft changes
     * the windows history causing a payment to hang on the payment page despite having been paid/completed
     * in some capacity.
     */
    cancelUpdateDraftCharge();

    try {
      // @todo separate flow for EVENT
      if (checkout.type === PAGE_TYPES.FORM) {
        setState(checkoutStates.SUBMITTING);

        try {
          // Get a fresh reCAPTCHA token right before submission
          const recaptchaToken = await executeRecaptcha('submit');

          await createSubmission({ recaptchaToken });
        } catch (error) {
          setFormError(error.message);
          setState(checkoutStates.INITIAL);
        }
      } else {
        try {
          if (!stripe) return;

          // Trigger form validation and wallet collection
          const { error: submitError } = await elements.submit();

          if (submitError) {
            throw submitError;
          }

          validateVariants();
          validateMinimumChargeAmount(payWhatYouWantPrice, product.currency, checkout.locale);
          validateHasStock();

          // Get a fresh reCAPTCHA token right before payment
          const recaptchaToken = await executeRecaptcha('checkout');

          if (product.type === 'subscription') {
            await createSubscription({ recaptchaToken });
          } else {
            await createPayment({ recaptchaToken });
          }
        } catch (error) {
          setFormError(error.message);
          setState(checkoutStates.INITIAL);
        }
      }
    } finally {
      /**
       * Reset the submission flag but with a brief delay to allow page navigation to occur first.
       * If the page navigation is slower than this 500ms delay, during testing what we see is an attempt from stripe to update the processing payment_intent.
       *
       * This PaymentIntent's amount could not be updated because it has a status of succeeded. You may only update the amount of a PaymentIntent with one of the following statuses: requires_payment_method, requires_confirmation, requires_action.
       */
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 500);
    }
  }

  const hasFields = checkout.fields?.length > 0;
  const showFields = hasFields && !isUpsell;

  const showPaymentOptions = checkout.paymentOptions?.length > 1;

  const hasExpressCheckout =
    checkout.paymentMethods.stripe.agpay &&
    checkout.paymentMethods.stripe.agpay?.enabled === true &&
    checkout.paymentMethods.stripe.agpay?.mode === 'express';
  const hasMerchantEnabledAppleGooglePay =
    seller.paymentGateways?.stripe?.enableAppleGooglePay === true;
  const hasCheckoutEnabledAppleGooglePay = checkout.paymentMethods.stripe.agpay?.enabled === true;
  const showExpressCheckout =
    !isUpsell &&
    !isScreenshot &&
    !isMeasurer &&
    hasExpressCheckout &&
    hasCheckoutEnabledAppleGooglePay &&
    hasMerchantEnabledAppleGooglePay &&
    showPaymentMethods;

  const showPriceBreakdown =
    (checkout.type === PAGE_TYPES.CHECKOUT || checkout.type === PAGE_TYPES.EVENT) &&
    checkout.showPriceBreakdown;

  const buttonBackgroundColor = getButtonBackgroundColor({
    buttonBackgroundColor: checkout.buttonBackgroundColor,
    brandColor: seller.brandColor,
  });

  const buttonTextColor = getButtonTextColor({
    buttonTextColor: checkout.buttonTextColor,
    buttonBackgroundColor: checkout.buttonBackgroundColor,
    brandColor: seller.brandColor,
  });

  return (
    <form
      onSubmit={e => {
        e.preventDefault();

        submitForm();
      }}
      onFocus={() => {
        hasFocusedRef.current = true;
      }}
      method="post"
      className={clsx('cp_form m-auto space-y-4 w-full pb-4')}
      ref={formRef}
    >
      {showExpressCheckout && (
        <div
          className="cp_express-checkout-wrapper"
          style={{ display: expressCheckoutLoaded ? 'block' : 'none' }}
        >
          <div className="cp_express-checkout cp_stripe-express-checkout-element-wrapper mb-4 relative flex flex-col space-y-2">
            <ExpressCheckoutElement mode="express" onReady={() => setExpressCheckoutLoaded(true)} />
          </div>

          <div className="cp_pay-with-card-wrapper flex overflow-hidden relative items-center py-2">
            <hr className="cp_pay-with-card-divider bg-gray-100 h-px w-full border-none" />
            <p className="cp_pay-with-card-text bg-white absolute -translate-x-1/2 left-1/2 whitespace-nowrap px-2 text-center text-gray-400 text-sm">
              {checkout.orPayWithCardText}
            </p>
          </div>
        </div>
      )}

      {showFields && (
        <div className="cp_form-fields">
          {(() => {
            const fieldKeys = Object.keys(form.fields);
            const sortedKeys = fieldKeys.sort(
              (a, b) => form.fields[a].order - form.fields[b].order,
            );
            return sortedKeys.map(key => (
              <FormField
                key={key}
                field={form.fields[key]}
                onChange={(value, meta) => {
                  setFormField(form.fields[key]._id, value, meta);
                }}
                onBlur={updateDraftCharge}
              />
            ));
          })()}
        </div>
      )}

      {product.type === 'charge' && product.payWhatYouWant && (
        <PayWhatYouWantField
          payWhatYouWantText={checkout.payWhatYouWantText}
          showPayWhatYouWantPrice={checkout.showPayWhatYouWantPrice}
          payWhatYouWantPlaceholderText={checkout.payWhatYouWantPlaceholderText}
          price={productPriceAmount}
          currency={product.currency}
          value={form.payWhatYouWant}
          locale={checkout.locale}
          onChange={value => {
            setFormStateByKey('payWhatYouWant', value);
            updateDraftChargeDebounced();
          }}
          onBlur={updateDraftCharge}
        />
      )}

      {/* {(checkout.billingAddressCollection ||
        checkout.shippingAddressCollection) && (
        <div className="cp_address-wrapper ">
          <AddressField />
        </div>
      )} */}

      {checkout.showCouponCodeField && (
        <div className="cp_coupon-wrapper">
          {!showCouponCodeField && (
            <div className="cp_coupon-add-wrapper">
              <button
                type="button"
                onClick={() => setShowCouponCodeField(true)}
                className="cp_coupon-add-button font-medium underline hover:text-gray-700"
              >
                {checkout.addDiscountCodeText}
              </button>
            </div>
          )}

          {showCouponCodeField && (
            <div className="cp_coupon-field-wrapper">
              <CouponField />
            </div>
          )}
        </div>
      )}

      {showPaymentOptions && <PaymentOptions />}

      {showPaymentMethods && (
        <div className="cp_payment-method-wrapper">
          <PaymentMethods />
        </div>
      )}

      {formError && formError.length > 0 && (
        <div className="cp_error-wrapper">
          <ErrorMessage className="cp_error">{formError}</ErrorMessage>
        </div>
      )}

      {showPriceBreakdown && <PriceBreakdown />}

      <div
        className={clsx('cp_pay-button-wrapper  mb-2', {
          'pt-2': !showPriceBreakdown,
        })}
      >
        {/* Prevent submitting form with enter key if there is payment collection */}
        {totalDueAmount > 0 && (
          <button type="submit" disabled className="hidden" aria-hidden="true" />
        )}

        <Button
          className="cp_button cp_pay-button whitespace-nowrap py-3 leading-snug"
          size="full"
          type="submit"
          appearance="black"
          backgroundColor={buttonBackgroundColor}
          color={buttonTextColor}
          disabled={submitIsDisabled}
        >
          {state === checkoutStates.SUBMITTING ||
          state === checkoutStates.CREATING_PAYMENT ||
          state === checkoutStates.CREATING_SUBSCRIPTION ||
          state === checkoutStates.CREATING_SUBMISSION ? (
            <Spinner
              className="cp_spinner"
              width="22px"
              height="22px"
              appearance="custom"
              style={{
                color: checkout.buttonTextColor,
              }}
            />
          ) : (
            getPayButtonText({
              page: checkout,
              product: product,
              subtotalAmount: subtotalAmount,
              totalDueAmount: totalDueAmount,
              formVariants: form.variants,
            })
          )}
        </Button>

        {isUpsell && (
          <div className="cp_upsell-decline-button-wrapper text-center mt-3">
            <button
              type="button"
              onClick={declineUpsell}
              className={clsx(
                'cp_upsell-decline-button whitespace-nowrap leading-snug text-black underline',
                {
                  'cursor-not-allowed': submitIsDisabled,
                },
              )}
              disabled={submitIsDisabled}
            >
              {checkout.upsellNoThanksText}
            </button>
          </div>
        )}
      </div>

      {(checkout.type === PAGE_TYPES.CHECKOUT || checkout.type === PAGE_TYPES.EVENT) &&
        checkout.showSecurePaymentsText &&
        !isScreenshot &&
        !isUpsell && (
          <div className="cp_secure-payments flex justify-center items-center gap-x-1 mt-4 text-sm text-gray-600">
            <LockClosedIcon className="cp_secure-payments-icon w-4 h-4" />
            <span className="cp_secure-payments-text">{checkout.securePaymentsText}</span>
          </div>
        )}
    </form>
  );
};

export default CheckoutForm;
